import { useEffect, useState } from 'react';
import { send } from '@/shared/messages';
import type { Chapter, Flashcard } from '@/shared/types';
import { CardReview } from '../components/CardReview';
import { CardFilterBar } from '../components/CardFilterBar';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';
import { ProgressBar, ProgressRing } from '@/components/Progress';

const FOCUS_STORAGE_KEY = 'cardsFocusChapterIds';

interface ChapterStats extends Chapter {
  cardCount: number;
  dueCount: number;
}

export function QueueRoute({
  activeTestId,
  refreshKey,
}: {
  activeTestId: string | null;
  refreshKey: number;
}) {
  const [chapters, setChapters] = useState<ChapterStats[]>([]);
  const [focusIds, setFocusIds] = useState<string[]>([]);
  const [focusLoaded, setFocusLoaded] = useState(false);
  const [current, setCurrent] = useState<Flashcard | null>(null);
  const [total, setTotal] = useState(0);
  const [reviewed, setReviewed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // One-shot: pull persisted focus selection before the first GET_DUE_CARDS
  // so the queue load doesn't briefly show all chapters then re-filter.
  useEffect(() => {
    let cancelled = false;
    chrome.storage.local.get(FOCUS_STORAGE_KEY).then((r) => {
      if (cancelled) return;
      const stored = r[FOCUS_STORAGE_KEY];
      if (Array.isArray(stored)) {
        setFocusIds(stored.filter((x): x is string => typeof x === 'string'));
      }
      setFocusLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!focusLoaded) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusLoaded, focusIds.join(','), activeTestId, refreshKey]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const chRes = await send({ type: 'LIST_CHAPTERS', testId: activeTestId ?? undefined });
      // Drop persisted filter ids that no longer exist or fall outside the
      // active test. Quietly clean storage so the chip count stays honest.
      const known = new Set(chRes.chapters.map((c) => c.id));
      const cleaned = focusIds.filter((id) => known.has(id));
      if (cleaned.length !== focusIds.length) {
        setFocusIds(cleaned);
        await chrome.storage.local.set({ [FOCUS_STORAGE_KEY]: cleaned });
      }
      // Scope the due queue to the active test: an explicit focus filter wins,
      // otherwise restrict to the test's chapters. With no active test, fall
      // through to all chapters (undefined filter).
      const testChapterIds = chRes.chapters.map((c) => c.id);
      const filter = cleaned.length
        ? cleaned
        : activeTestId
          ? testChapterIds
          : undefined;
      let dueRes: { cards: Flashcard[]; total: number };
      if (filter && filter.length === 0) {
        // Active test with no chapters — nothing to review.
        dueRes = { cards: [], total: 0 };
      } else {
        dueRes = await send({ type: 'GET_DUE_CARDS', chapterIds: filter });
      }
      setChapters(chRes.chapters);
      setTotal(dueRes.total);
      setCurrent(dueRes.cards[0] ?? null);
      setReviewed(0);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function rate(rating: 1 | 2 | 3 | 4, durationMs: number) {
    if (!current) return;
    try {
      // Keep the next card within the same scope as the queue: explicit focus
      // filter, else the active test's chapters (already in `chapters`).
      const nextFilter = focusIds.length
        ? focusIds
        : activeTestId
          ? chapters.map((c) => c.id)
          : undefined;
      const r = await send({
        type: 'RATE_CARD',
        cardId: current.id,
        rating,
        durationMs,
        chapterIds: nextFilter,
      });
      setReviewed((n) => n + 1);
      setCurrent(r.nextCard ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function updateFocus(next: string[]) {
    setFocusIds(next);
    await chrome.storage.local.set({ [FOCUS_STORAGE_KEY]: next });
  }

  const cardCounts: Record<string, number> = {};
  const dueCounts: Record<string, number> = {};
  for (const c of chapters) {
    cardCounts[c.id] = c.cardCount;
    dueCounts[c.id] = c.dueCount;
  }

  return (
    <div className="flex h-full flex-col">
      <CardFilterBar
        chapters={chapters}
        cardCounts={cardCounts}
        dueCounts={dueCounts}
        selected={focusIds}
        onChange={updateFocus}
      />
      {loading ? (
        <div className="p-4 text-sm text-slate-500">Loading queue…</div>
      ) : error ? (
        <div className="space-y-2 p-4">
          <Alert>Error: {error}</Alert>
          <Button size="xs" onClick={load}>
            Retry
          </Button>
        </div>
      ) : !current ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
          {reviewed > 0 && (
            <ProgressRing
              value={reviewed}
              max={reviewed}
              size={64}
              thickness={7}
              tone="success"
              valueText={String(reviewed)}
              label="done"
              className="mb-1"
            />
          )}
          <h2 className="text-lg font-semibold">You're done for now.</h2>
          <p className="text-sm text-slate-600">
            {reviewed > 0
              ? `Reviewed ${reviewed} card${reviewed === 1 ? '' : 's'} this session. Come back tomorrow — that's the spacing effect at work.`
              : focusIds.length
                ? 'Nothing due in the focused chapters. Try clearing the filter or come back later.'
                : 'Nothing due. Add a chapter to generate flashcards.'}
          </p>
          <Button className="mt-2" onClick={load}>
            Check again
          </Button>
        </div>
      ) : (
        <div className="space-y-4 px-4 pt-4 pb-safe-4">
          <header className="space-y-1.5">
            <ProgressBar
              value={reviewed}
              max={total}
              size="sm"
              valueText={`${reviewed}/${total} reviewed`}
            />
            <div className="text-right font-mono text-[10px] text-slate-500">
              {current.fsrs.state} · reps {current.fsrs.reps}, lapses {current.fsrs.lapses}
            </div>
          </header>
          <CardReview card={current} onRate={rate} />
        </div>
      )}
    </div>
  );
}
